import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ymGoal } from '@/lib/analytics';
import { ASPECT_SYMBOL, PlanetIcon, TONE_CLASSES } from '@/pages/horary/HoraryFact';

/**
 * КРУГ ДНЯ — сутки кругом, как натальная карта, хорар и колесо Любовь/Деньги.
 *
 * Геометрия натальной карты: 00:00 слева (там, где Асцендент), ход ПРОТИВ
 * часовой стрелки — 06:00 внизу, 12:00 справа, 18:00 вверху. Риска на каждый
 * час, подпись «3:00» каждые 3 часа (на ПК — каждый час). Стрелка «сейчас» из центра.
 *
 * Слои изнутри наружу:
 *   диск, светло-голубой   — НАСТРОЕНИЕ: аспекты Луны точками в момент точности;
 *   кольцо, светло-жёлтое  — СОБЫТИЯ: точные аспекты дня точками в момент пика;
 *   обводки, сиреневые     — ФОН: каждый процесс — своя обводка на полный круг,
 *                            не больше трёх (сильнейшие), остальные — «и ещё N».
 *
 * Данные — ответ POST /api/daily/timeline (services/day_timeline.py), новых
 * расчётов нет. Лесенка тарифа — серверная: чего сервер не прислал, того и нет.
 * Гостю открыты 3 точки, ближайшие к текущему часу; остальные приходят
 * только временем ({ locked: true }) — серая точка и кнопка регистрации.
 * compact — мини-круг карточки «Твой день сегодня» на главной.
 */

const SIZE = 400;
const C = SIZE / 2;
// Поле вокруг круга под подписи часов «12:00»: у мини-круга шрифт крупнее.
const PAD_FULL = 28;
const PAD_COMPACT = 32;
const R_MOOD = 94;          // диск Настроения: 0..R_MOOD
const R_EV = 134;           // кольцо Событий: R_MOOD..R_EV
const R_MOOD_DOT = 66;
const R_EV_DOT = 114;
const BG_R = [144, 156, 168];
// Невидимые полосы попадания обводок фона: от середины до середины между
// соседними обводками (не уже ~12 px), у внешней — наружу до рисок часов.
// Рисуются ПОВЕРХ рисок: иначе риски перехватывают мышь у внешней обводки.
const BG_HIT = [[136, 150], [150, 162], [162, 180]];
const BG_W = 6;
const R_TICK = 174;
// Подпись часа стоит снаружи рисок: её ближний к кругу край — на
// LABEL_GAP от конца риски, в какую сторону круга ни смотри.
const LABEL_GAP = 5;
const DOT_R = 7;
// Точки одного часа разводим по углу на 3° (12 минут), а упёршиеся друг в
// друга — ещё и по радиусу, чтобы кружки не легли один на другой.
const GAP_MIN = 12;

const COLOR = { harmonic: '#10b981', tense: '#f43f5e', neutral: '#0ea5e9' };
const LOCKED_DOT = '#94a3b8';
const BG_COLOR = '#a78bfa';
const BG_LOCKED = '#cbd5e1';
const TONE = { harmonic: '+', tense: '-', neutral: 'conj' };
const CARD_BAR = {
  harmonic: 'border-l-emerald-400',
  tense: 'border-l-rose-400',
  neutral: 'border-l-sky-400',
};

const DESTINY_PRICING_URL =
  '/pricing?utm_source=site&utm_medium=daily_results&utm_campaign=daily_timeline';
export const REGISTER_EVENTS_LINE = 'Регистрация в 1 клик — открыть аспекты дня';

// «Ещё N событий дня — зарегистрируйся в 1 клик» — у закрытых точек гостя.
export function moreEventsLine(n) {
  const tail = n % 10;
  const teen = n % 100 >= 11 && n % 100 <= 14;
  const word = tail === 1 && !teen ? 'событие' : (tail >= 2 && tail <= 4 && !teen ? 'события' : 'событий');
  return `Ещё ${n} ${word} дня — зарегистрируйся в 1 клик`;
}

function hourLabel(h) {
  return `${h}:00`;
}

// Центр подписи часа: ширина текста оценена по цифрам (~0.6 em) и двоеточию
// (~0.3 em), высота — 0.75 em. Подпись отодвигается наружу, пока ближайшая к
// центру круга точка её прямоугольника не окажется на LABEL_GAP дальше конца
// рисок — так «12:00» справа, «18:00» сверху и диагональные «3:00», «21:00»
// не залезают ни на риски, ни на обводки.
function labelPt(h, fontSize) {
  const text = hourLabel(h);
  const hw = (((text.length - 1) * 0.6 + 0.3) * fontSize) / 2;
  const hh = (0.75 * fontSize) / 2;
  const minD = R_TICK + 2 + LABEL_GAP;
  let r = minD;
  for (;;) {
    const [x, y] = pt(r, h * 60);
    const nx = Math.max(0, Math.abs(x - C) - hw);
    const ny = Math.max(0, Math.abs(y - C) - hh);
    if (Math.hypot(nx, ny) >= minD) return [x, y];
    r += 0.5;
  }
}

function minutesOf(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

// Минуты суток → точка круга: 00:00 слева, против часовой (06:00 внизу).
function pt(r, minutes) {
  const t = (minutes / 1440) * 2 * Math.PI;
  return [C - r * Math.cos(t), C + r * Math.sin(t)];
}

// Раскладка точек слоя: по времени, упёршиеся — на GAP_MIN дальше по углу
// и через одну — ступенькой по радиусу.
function layout(items, baseR) {
  const sorted = items
    .map((it) => ({ ...it, m: minutesOf(it.time) }))
    .filter((it) => it.m != null)
    .sort((a, b) => a.m - b.m);
  let prev = null;
  let step = 0;
  return sorted.map((it) => {
    let pos = it.m;
    if (prev != null && pos - prev < GAP_MIN) {
      pos = prev + GAP_MIN;
      step += 1;
    } else {
      step = 0;
    }
    prev = pos;
    const r = baseR + (step % 2 === 1 ? -DOT_R - 2 : 0);
    const [x, y] = pt(r, pos);
    return { ...it, x, y };
  });
}

function Fact({ fact, polarity }) {
  if (!fact || !fact.p1) return null;
  const tone = TONE_CLASSES[TONE[polarity]] || TONE_CLASSES.conj;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs leading-none ${tone}`}
      data-testid="circle-fact">
      <PlanetIcon planet={fact.p1} />
      <span aria-hidden>{ASPECT_SYMBOL[fact.aspect] || '·'}</span>
      {fact.p2 ? <PlanetIcon planet={fact.p2} /> : (
        <span className="text-[10px] font-semibold">{fact.p2_label}</span>
      )}
    </span>
  );
}

function GoTo({ k, onGoTo, hasTarget }) {
  if (!onGoTo || !hasTarget || !hasTarget(k)) return null;
  return (
    <button type="button" onClick={() => onGoTo(k)}
      className="ml-auto text-xs font-semibold text-violet-700 hover:text-violet-900"
      data-testid={`circle-goto-${k}`}>
      В разборе ↓
    </button>
  );
}

// Содержимое всплывашки: время и аспект человеческим ярлыком, тема,
// «Что делать», «В разборе ↓». Лесенка — по тому, что прислал сервер.
function TipBody({ sel, data, onRegister, onGoTo, hasTarget }) {
  // Список всего фона — тап по строке «Идут N процессов» под кругом. Тексты
  // те же, что у всплывашки отдельной обводки; сервер присылает их только ПРО,
  // остальным — та же строка-замок, что и у обводки.
  if (sel.kind === 'destiny_all' && (data.destiny.items || []).length) {
    return (
      <ul className="max-h-[50vh] space-y-2 overflow-y-auto" data-testid="circle-tip-destiny-all">
        {data.destiny.items.map((it) => (
          <li key={it.key} className={`border-l-4 pl-3 ${CARD_BAR[it.polarity] || CARD_BAR.neutral}`}
            data-testid={`circle-tip-destiny-all-${it.key}`}>
            <p className="text-xs font-semibold text-violet-700">{it.title_line}</p>
            {it.line && it.line !== it.title_line && (
              <p className="mt-0.5 text-sm font-medium text-gray-800 leading-snug">{it.line}</p>
            )}
            {it.end_line && <p className="mt-1 text-xs text-gray-500">Срок: {it.end_line}</p>}
            {it.todo && (
              <p className="mt-1 text-sm text-gray-600 leading-snug"><b>Что делать:</b> {it.todo}</p>
            )}
            <div className="mt-1.5 flex items-center gap-2">
              <Fact fact={it.fact} polarity={it.polarity} />
              <GoTo k={it.key} onGoTo={onGoTo} hasTarget={hasTarget} />
            </div>
          </li>
        ))}
      </ul>
    );
  }
  if (sel.kind === 'destiny' || sel.kind === 'destiny_all') {
    const d = data.destiny;
    const it = (d.items || [])[sel.index];
    if (it) {
      return (
        <div className={`border-l-4 pl-3 ${CARD_BAR[it.polarity] || CARD_BAR.neutral}`}
          data-testid={`circle-tip-destiny-${it.key}`}>
          <p className="text-xs font-semibold text-violet-700">{it.title_line}</p>
          {it.line && <p className="mt-0.5 text-sm font-medium text-gray-800 leading-snug">{it.line}</p>}
          {it.end_line && <p className="mt-1 text-xs text-gray-500">Срок: {it.end_line}</p>}
          {it.todo && (
            <p className="mt-1 text-sm text-gray-600 leading-snug"><b>Что делать:</b> {it.todo}</p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <Fact fact={it.fact} polarity={it.polarity} />
            <GoTo k={it.key} onGoTo={onGoTo} hasTarget={hasTarget} />
          </div>
        </div>
      );
    }
    return (
      <div data-testid="circle-tip-destiny-locked">
        <p className="text-sm font-medium text-gray-800">{d.count_line}</p>
        {d.locked_label && (
          <a href={DESTINY_PRICING_URL}
            className="mt-1 inline-block text-sm font-semibold text-violet-700 underline hover:text-violet-900"
            data-testid="circle-destiny-pro">
            {d.locked_label}
          </a>
        )}
      </div>
    );
  }
  const it = sel.item;
  if (!it) return null;
  if (it.locked) {
    const n = (data.guest && data.guest.locked) || 0;
    return (
      <div data-testid="circle-tip-locked">
        <p className="mb-2 text-xs text-gray-500">{it.time}</p>
        {onRegister ? (
          <button type="button" onClick={onRegister}
            className="w-full rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold leading-snug text-white hover:bg-violet-700"
            data-testid="circle-register">
            {moreEventsLine(n)}
          </button>
        ) : <p className="text-sm font-medium text-gray-800">{moreEventsLine(n)}</p>}
      </div>
    );
  }
  if (sel.kind === 'event' && data.events.locked) {
    return (
      <div>
        <p className="mb-2 text-xs text-gray-500">{it.time}</p>
        {onRegister && (
          <button type="button" onClick={onRegister}
            className="w-full rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700"
            data-testid="circle-register">
            {REGISTER_EVENTS_LINE}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={`border-l-4 pl-3 ${CARD_BAR[it.polarity] || CARD_BAR.neutral}`}
      data-testid={`circle-tip-${it.key}`}>
      <p className="text-xs font-semibold text-gray-500">
        {it.time}{it.title ? ` · ${it.title}` : ''}
      </p>
      {it.what && <p className="mt-0.5 text-sm font-medium text-gray-800 leading-snug">{it.what}</p>}
      {it.todo && (
        <p className="mt-1 text-sm text-gray-600 leading-snug"><b>Что делать:</b> {it.todo}</p>
      )}
      {it.sphere && (
        <p className="mt-1 text-xs text-violet-900 leading-snug">
          <span className="font-semibold">{data.sphere_label}:</span> {it.sphere}
        </p>
      )}
      <div className="mt-1.5 flex items-center gap-2">
        <Fact fact={it.fact} polarity={it.polarity} />
        <GoTo k={it.key} onGoTo={onGoTo} hasTarget={hasTarget} />
      </div>
    </div>
  );
}

// Кольцо (для полос попадания): внешний и внутренний круги, чётно-нечётная заливка.
function annulus(r0, r1) {
  const ring = (r, sweep) => `M${C - r},${C} a${r},${r} 0 1,${sweep} ${2 * r},0 a${r},${r} 0 1,${sweep} ${-2 * r},0 Z`;
  return `${ring(r1, 0)} ${ring(r0, 1)}`;
}

// ПК (от 768 px) — всплывашка плавает у точки; телефон — карточкой под кругом.
function useDesktop() {
  const q = '(min-width: 768px)';
  const get = () => (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(q).matches : false);
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const upd = () => setOn(mq.matches);
    upd();
    if (mq.addEventListener) mq.addEventListener('change', upd); else mq.addListener(upd);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', upd); else mq.removeListener(upd);
    };
  }, []);
  return on;
}

// Точки Луны: уникальные аспекты из часов ленты (аспект стоит в нескольких
// часах окна — точка одна, в минуту точности). Текст у точки — с сервера
// (services/day_timeline, запасной — services/moon_texts); своего словаря
// у круга нет.
function moonItems(data) {
  const seen = new Map();
  (data.mood.hours || []).forEach((h) => h.items.forEach((it) => {
    if (!seen.has(it.key)) seen.set(it.key, it);
  }));
  return [...seen.values()];
}

function eventItems(data) {
  const out = [];
  (data.events.markers || []).forEach((mk) => mk.items.forEach((it) => {
    out.push({ ...it, polarity: it.polarity || mk.polarity, time: it.time || mk.time });
  }));
  return out;
}

export default function DayCircle({
  data, now, compact = false, linkKey, onRegister, onGoTo, hasTarget,
}) {
  const boxRef = useRef(null);
  const tipRef = useRef(null);
  const bgLineRef = useRef(null);
  const [sel, setSel] = useState(null);   // {kind, item|index, x, y, pinned}
  const [boxW, setBoxW] = useState(0);
  // Подсветка от тапа по карточке в разборе гаснет, как только открыли
  // что-то на круге: активной остаётся одна точка/обводка.
  const [mutedLink, setMutedLink] = useState(null);
  const desktop = useDesktop();
  const pad = compact ? PAD_COMPACT : PAD_FULL;

  const moon = useMemo(() => layout(moonItems(data), R_MOOD_DOT), [data]);
  const events = useMemo(() => layout(eventItems(data), R_EV_DOT), [data]);
  const d = data.destiny || {};
  const ringCount = Math.min(d.count || 0, 3);
  const moreBg = Math.max(0, (d.count || 0) - 3);
  const bgOpen = !!(d.items && d.items.length);
  const bgColor = data.access === 'guest' ? BG_LOCKED : BG_COLOR;

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const upd = () => setBoxW(el.getBoundingClientRect().width || 0);
    upd();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Тап мимо круга и всплывашки — закрыть.
  useEffect(() => {
    if (!sel) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)
        && !(bgLineRef.current && bgLineRef.current.contains(e.target))
        && !(tipRef.current && tipRef.current.contains(e.target))) setSel(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [sel]);

  // Новая связка из разбора — прежняя всплывашка круга закрывается.
  useEffect(() => {
    setSel(null);
    setMutedLink(null);
  }, [linkKey]);

  // Клик/тап всегда ЗАМЕНЯЕТ открытую всплывашку, какой бы она ни была.
  const open = useCallback((next, viaTap) => {
    setSel({ ...next, pinned: viaTap });
    setMutedLink(linkKey || null);
    if (viaTap) ymGoal('timeline_tap', { track: next.kind });
  }, [linkKey]);
  const hoverOpen = (next) => (e) => {
    if (e.pointerType !== 'mouse') return;
    setSel((cur) => {
      if (cur && cur.pinned) return cur;
      const same = cur && cur.kind === next.kind
        && (next.kind === 'destiny' ? cur.index === next.index
          : cur.item && next.item && cur.item.key === next.item.key);
      return same ? cur : { ...next, pinned: false };
    });
  };

  // Телефон: всплывашка под кругом докручивается так, чтобы её низ был выше
  // плавающей кнопки помощи «?» (fixed, 48 px у нижнего края).
  useEffect(() => {
    if (!sel || !sel.pinned || (desktop && sel.kind !== 'destiny_all') || !tipRef.current) return;
    const r = tipRef.current.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const limit = vh - 88;
    if (r.bottom > limit) window.scrollBy({ top: r.bottom - limit, behavior: 'instant' });
  }, [sel, desktop]);
  const keyOpen = (next) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(next, true); }
  };

  const isLinked = (k) => !!linkKey && k === linkKey && linkKey !== mutedLink;
  const isSel = (kind, v) => sel && sel.kind === kind
    && (kind === 'destiny' ? sel.index === v : sel.item && sel.item.key === v);

  const dot = (kind, it, color) => {
    const next = { kind, item: it, x: it.x, y: it.y };
    const on = isSel(kind, it.key) || isLinked(it.key);
    return (
      <g key={`${kind}-${it.key}`} role="button" tabIndex={0} className="cursor-pointer outline-none"
        onClick={() => open(next, true)} onPointerEnter={hoverOpen(next)} onKeyDown={keyOpen(next)}
        aria-label={`${it.time}${it.title ? ` ${it.title}` : ''}`}
        data-testid={`circle-dot-${kind}-${it.key}`} data-linked={isLinked(it.key) ? '1' : undefined}>
        <circle cx={it.x} cy={it.y} r={DOT_R + 7} fill="transparent" />
        {on && <circle cx={it.x} cy={it.y} r={DOT_R + 4} fill="none" stroke="#7c3aed" strokeWidth="2.5" />}
        <circle cx={it.x} cy={it.y} r={DOT_R} fill={color} stroke="#fff" strokeWidth="2" />
      </g>
    );
  };

  // Пиксели всплывашки из координат SVG.
  const scale = boxW ? boxW / (SIZE + 2 * pad) : 1;
  // Узкий экран (телефон, мини-круг) — всплывашка встаёт карточкой прямо под
  // кругом: над точкой в нижней половине ей не хватает места, и она уезжала
  // бы за край блока. ПК — плавает у точки, как на колесе Любовь/Деньги.
  const docked = !desktop || (sel && sel.kind === 'destiny_all');
  let tipStyle = null;
  if (sel && boxW && !docked) {
    const tipW = Math.min(288, boxW - 16);
    const px = (sel.x + pad) * scale;
    const py = (sel.y + pad) * scale;
    const left = Math.min(Math.max(px - tipW / 2, 8), boxW - tipW - 8);
    tipStyle = py < boxW / 2
      ? { left, width: tipW, top: py + 14 }
      : { left, width: tipW, bottom: boxW - py + 14 };
  }

  const tipEl = sel && (tipStyle || docked) ? (
    <div className={docked
      ? 'relative z-20 mt-1 rounded-xl border border-slate-200 bg-white p-3 shadow-md'
      // Плавающая пропускает клики сквозь себя к точкам под ней (клик по
      // соседней точке заменяет всплывашку), кликабельны только её
      // кнопки и ссылки.
      : 'pointer-events-none absolute z-20 rounded-xl border border-slate-200 bg-white p-3 shadow-lg [&_a]:pointer-events-auto [&_button]:pointer-events-auto'}
      ref={tipRef} style={docked ? undefined : tipStyle} data-testid="circle-tip">
      <div className="mb-1 flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
          {sel.kind === 'mood' ? 'Настроение' : sel.kind === 'event' ? 'События'
            : sel.kind === 'destiny_all' ? 'Фоновые влияния' : 'Фон'}
        </p>
        <button type="button" onClick={() => setSel(null)} aria-label="Закрыть"
          className="-mt-1 px-1 text-base leading-none text-gray-400 hover:text-gray-600">×</button>
      </div>
      <TipBody sel={sel} data={data} onRegister={onRegister} onGoTo={(k) => { setSel(null); onGoTo(k); }}
        hasTarget={hasTarget} />
    </div>
  ) : null;
  const listOpen = !!(sel && sel.kind === 'destiny_all');

  const hours = Array.from({ length: 24 }, (_, h) => h);
  const labelHours = compact ? [0, 6, 12, 18] : [0, 3, 6, 9, 12, 15, 18, 21];

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="day-circle">
      <div ref={boxRef} className={`relative mx-auto w-full ${compact ? 'max-w-[320px]' : 'max-w-[440px]'}`}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') setSel((cur) => (cur && !cur.pinned ? null : cur));
        }}>
        <svg viewBox={`${-pad} ${-pad} ${SIZE + 2 * pad} ${SIZE + 2 * pad}`}
          className="block h-auto w-full select-none" data-testid="day-circle-svg">
          <defs>
            <marker id="day-circle-arrow" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 z" fill="#1e293b" />
            </marker>
          </defs>

          {/* ФОН — обводки на полный круг, от внутренней к внешней (только
              рисунок; мышь и тап ловят полосы попадания ниже, поверх рисок). */}
          {Array.from({ length: ringCount }, (_, i) => {
            const it = (d.items || [])[i];
            const k = it ? it.key : `bg-${i}`;
            const on = isSel('destiny', i) || (it && isLinked(it.key));
            return (
              <g key={k} pointerEvents="none"
                data-testid={`circle-bg-ring-${i}`} data-linked={it && isLinked(it.key) ? '1' : undefined}>
                <circle cx={C} cy={C} r={BG_R[i]} fill="none" pointerEvents="none"
                  stroke={on ? '#6d28d9' : bgColor} strokeWidth={on ? BG_W + 2 : BG_W} />
              </g>
            );
          })}

          {/* СОБЫТИЯ — светло-жёлтое кольцо; НАСТРОЕНИЕ — светло-голубой диск. */}
          <circle cx={C} cy={C} r={(R_MOOD + R_EV) / 2} fill="none" stroke="#fef3c7"
            strokeWidth={R_EV - R_MOOD} />
          <circle cx={C} cy={C} r={R_MOOD} fill="#e0f2fe" />
          <circle cx={C} cy={C} r={R_EV} fill="none" stroke="#fde68a" strokeWidth="1" />
          <circle cx={C} cy={C} r={R_MOOD} fill="none" stroke="#bae6fd" strokeWidth="1" />

          {/* Часы: риска на каждый час, спица через три часа. */}
          {hours.map((h) => {
            const major = h % 3 === 0;
            const [x0, y0] = pt(major ? R_TICK - 10 : R_TICK - 5, h * 60);
            const [x1, y1] = pt(R_TICK + 2, h * 60);
            return (
              <line key={`t-${h}`} x1={x0} y1={y0} x2={x1} y2={y1}
                stroke={major ? '#475569' : '#94a3b8'} strokeWidth={major ? 2 : 1} />
            );
          })}
          {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => {
            const [x0, y0] = pt(R_MOOD * 0.45, h * 60);
            const [x1, y1] = pt(R_EV, h * 60);
            return <line key={`s-${h}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke="#ffffff" strokeWidth="1.5" />;
          })}
          {labelHours.map((h) => {
            const fs = compact ? 18 : 16;
            const [x, y] = labelPt(h, fs);
            return (
              <text key={`l-${h}`} x={x} y={y} textAnchor="middle" dominantBaseline="central"
                fontSize={fs} fontWeight="600" fill="#334155" data-testid={`circle-hour-${h}`}>{hourLabel(h)}</text>
            );
          })}
          {!compact && hours.filter((h) => h % 3 !== 0).map((h) => {
            const [x, y] = labelPt(h, 11);
            return (
              <text key={`lm-${h}`} x={x} y={y} textAnchor="middle" dominantBaseline="central"
                fontSize="11" fill="#94a3b8" className="hidden md:block">{hourLabel(h)}</text>
            );
          })}

          {/* Полосы попадания обводок фона — поверх рисок и подписей часов. */}
          {Array.from({ length: ringCount }, (_, i) => {
            const it = (d.items || [])[i];
            const [x, y] = pt(BG_R[i], 1080);
            const next = { kind: 'destiny', index: i, x, y };
            // Всплывашка фона — у того места обводки, куда навели / кликнули.
            const at = (e) => {
              const box = boxRef.current && boxRef.current.getBoundingClientRect();
              if (!box || !box.width || e.clientX == null) return next;
              const k = box.width / (SIZE + 2 * pad);
              return { ...next, x: (e.clientX - box.left) / k - pad, y: (e.clientY - box.top) / k - pad };
            };
            return (
              <path key={`hit-${i}`} d={annulus(BG_HIT[i][0], BG_HIT[i][1])} fill="transparent"
                fillRule="evenodd" pointerEvents="all" role="button" tabIndex={0}
                className="cursor-pointer outline-none"
                onClick={(e) => open(at(e), true)} onPointerEnter={(e) => hoverOpen(at(e))(e)}
                onKeyDown={keyOpen(next)}
                aria-label={it ? it.title_line : d.count_line}
                data-testid={`circle-bg-${i}`} />
            );
          })}

          {/* Стрелка «сейчас» — из центра по местному времени. */}
          {now != null && (() => {
            const [x0, y0] = pt(18, now);
            const [x1, y1] = pt(R_EV + 4, now);
            return (
              <g pointerEvents="none" data-testid="circle-now">
                <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#1e293b" strokeWidth="2.5"
                  strokeLinecap="round" markerEnd="url(#day-circle-arrow)" />
                <circle cx={C} cy={C} r="5" fill="#1e293b" />
              </g>
            );
          })()}

          {moon.map((it) => dot('mood', it,
            it.locked ? LOCKED_DOT : (COLOR[it.polarity] || COLOR.neutral)))}
          {events.map((it) => dot('event', it,
            (data.events.locked || it.locked) ? LOCKED_DOT : (COLOR[it.polarity] || COLOR.neutral)))}
        </svg>

        {!listOpen && tipEl}

      </div>

      {/* Легенда слоёв: название жирным, пояснение обычным; кружки крупные,
          чтобы цвет был виден с телефона. */}
      <ul className={`mx-auto max-w-[440px] space-y-1.5 ${compact ? 'text-xs' : 'text-[13px]'} text-gray-600 leading-snug`}
        data-testid="circle-legend">
        <li className="flex items-start gap-2">
          <span className="mt-px inline-block h-6 w-6 shrink-0 rounded-full bg-sky-100 ring-1 ring-sky-300" />
          <span><b className="font-semibold text-gray-800">Настроение дня</b> — голубой диск — это Луна: как меняется твоё настроение и самочувствие в течение суток.</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-px inline-block h-6 w-6 shrink-0 rounded-full bg-amber-100 ring-1 ring-amber-300" />
          <span><b className="font-semibold text-gray-800">События дня</b> — жёлтое кольцо — аспекты быстрых планет: в эти часы вероятнее всего что-то происходит — встречи, разговоры, решения.</span>
        </li>
        <li className="flex items-start gap-2">
          <span className={`mt-px inline-block h-6 w-6 shrink-0 rounded-full border-[3px] ${data.access === 'guest' ? 'border-slate-300' : 'border-violet-400'}`} />
          <span><b className="font-semibold text-gray-800">Фоновые судьбоносные влияния</b> — сиреневые обводки — медленные планеты: долгие процессы, которые идут в твоей жизни неделями и месяцами и окрашивают весь день.</span>
        </li>
      </ul>
      {(d.count || 0) > 0 && (
        <p ref={bgLineRef} className={`text-center ${compact ? 'text-xs' : 'text-[13px]'} text-violet-900`} data-testid="circle-bg-line">
          {/* Тап по строке — всплывашка со всем фоном списком (и с тем, что
              не уместилось на круге тремя обводками). */}
          <button type="button"
            onClick={() => (sel && sel.kind === 'destiny_all'
              ? setSel(null)
              : open({ kind: 'destiny_all', x: C, y: SIZE }, true))}
            aria-expanded={!!(sel && sel.kind === 'destiny_all')}
            className="underline decoration-dotted decoration-violet-400 underline-offset-2 hover:text-violet-700"
            data-testid="circle-bg-line-open">
            {bgOpen
              ? (moreBg > 0 ? `Фон: ${d.count_line}, на круге три сильнейших — и ещё ${moreBg}` : d.count_line)
              : d.count_line}
          </button>
          {!bgOpen && d.locked_label && (
            <>
              {' · '}
              <a href={DESTINY_PRICING_URL} className="font-semibold underline hover:text-violet-700">
                {d.locked_label}
              </a>
            </>
          )}
        </p>
      )}
      {listOpen && <div className="mx-auto max-w-[440px]">{tipEl}</div>}
      {(d.count || 0) === 0 && !compact && (
        <p className="text-center text-[13px] text-gray-500">Фоновых процессов нет</p>
      )}
    </div>
  );
}
